import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useColorModeValue,
  useToast,
  Wrap,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";

const DEFAULT_RESERVED = [
  "abuse", "account", "accounts", "admin", "affiliate", "affiliates", "analytics",
  "api", "app", "assets", "auth", "autodiscover", "backoffice", "beta", "billing",
  "blog", "booking", "bookings", "bounce-zem", "calendar", "careers", "cdn", "center",
  "config", "console", "contact", "customer", "dashboard", "delivery", "demo", "dev",
  "developer", "developers", "dns", "docs", "download", "downloads", "email", "fallback",
  "fastdesk", "files", "franchise", "franchises", "ftp", "go", "help", "helpdesk", "home",
  "hostmaster", "hq", "hub", "images", "imap", "img", "info", "internal", "invoice",
  "invoices", "jobs", "kds", "kiosk", "kitchen", "legal", "link", "links", "login",
  "loyalty", "mail", "manager", "media", "mobile", "my", "notifications", "notify", "ns",
  "ns1", "ns2", "nuagepos", "oauth", "office", "order", "orders", "origin", "partner",
  "partners", "pay", "payment", "payments", "portal", "pos", "postmaster", "preview",
  "print", "printer", "privacy", "proxy", "receipt", "receipts", "register", "report",
  "reports", "reservation", "reservations", "rewards", "root", "sales", "sandbox",
  "security", "settings", "signup", "smtp", "sso", "ssl", "staff", "stage", "staging",
  "static", "status", "super", "superadmin", "support", "system", "terms", "test",
  "track", "tracking", "vendor", "vendors", "webhook", "webhooks", "webmaster",
  "webmail", "wiki", "worker", "workers", "www",
  "logout", "checkout", "cart", "shop", "store", "faq", "news", "about",
  "development", "cpanel", "uploads",
];

const DEFAULT_PROFANE = [
  "anal", "anus", "arse", "ass", "asshole", "bastard", "bitch", "bollock",
  "boob", "butt", "clitoris", "cock", "coon", "crap", "cunt", "damn", "dick",
  "dildo", "dyke", "fag", "faggot", "felch", "fellate", "fellatio", "flange",
  "fuck", "fucker", "gangbang", "gay", "genital", "jerk", "jizz", "knob",
  "labia", "lmao", "lmfao", "mothafucka", "mothafucker", "muff", "nigga",
  "nigger", "penis", "piss", "poop", "prick", "pube", "pussy", "queer",
  "scrotum", "semen", "sex", "shitian", "shit", "shithole", "slut", "smegma",
  "spunk", "tit", "tosser", "turd", "twat", "vagina", "wank", "whore", "wtf",
  "batard", "bordel", "ciboire", "calisse", "calice", "chier",
  "chienne", "connard", "conne", "couille", "crouille", "cul", "encule",
  "fion", "foutre", "merde", "nique", "pute", "putain", "salope",
  "tapette", "trompette", "trouduc",
];

const foldWord = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/4/g, "a")
    .replace(/3/g, "e")
    .replace(/0/g, "o")
    .replace(/1/g, "i")
    .replace(/5/g, "s")
    .replace(/7/g, "t")
    .replace(/[-_.\s]/g, "");

const DEFAULT_PROFANE_SET = new Set(DEFAULT_PROFANE.map(foldWord));

const uniqueWords = (words: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  words.forEach((word) => {
    const folded = foldWord(String(word || ""));
    if (!folded || seen.has(folded)) return;
    seen.add(folded);
    result.push(folded);
  });
  return result;
};

const ForbiddenSubdomainSettings: React.FC = () => {
  const toast = useToast();
  const [reservedWords, setReservedWords] = useState<string[]>([]);
  const [reservedInput, setReservedInput] = useState("");
  const [customProfane, setCustomProfane] = useState<string[]>([]);
  const [profaneInput, setProfaneInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const snapshot = await getDoc(doc(db, "systemConfig", "forbiddenSubdomains"));
        if (snapshot.exists()) {
          const data = snapshot.data();
          const savedReserved = Array.isArray(data.reserved) ? data.reserved : [];
          const reserved = data.reservedManaged === true && savedReserved.length
            ? uniqueWords(savedReserved)
            : uniqueWords([...DEFAULT_RESERVED, ...savedReserved]);
          setReservedWords(reserved);
          const saved = Array.isArray(data.profane) ? data.profane : [];
          setCustomProfane(saved.filter((word: string) => !DEFAULT_PROFANE_SET.has(foldWord(word))));
        } else {
          setReservedWords(uniqueWords(DEFAULT_RESERVED));
          setCustomProfane([]);
        }
      } catch (error) {
        console.error("Error loading forbidden subdomains:", error);
        setReservedWords(uniqueWords(DEFAULT_RESERVED));
        setCustomProfane([]);
        toast({
          title: "Error",
          description: "Failed to load forbidden words. Showing the starting list.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const addReservedWord = () => {
    const word = foldWord(reservedInput);
    if (!word || !/^[a-z0-9]+$/.test(word)) {
      toast({
        title: "Invalid word",
        description: "Use letters and numbers only.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (reservedWords.some((item) => foldWord(item) === word)) {
      toast({
        title: "Already in the list",
        description: word,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setReservedWords((items) => [...items, word]);
    setReservedInput("");
  };

  const addProfaneWord = () => {
    const word = foldWord(profaneInput);
    if (!word || !/^[a-z0-9]+$/.test(word)) {
      toast({
        title: "Invalid word",
        description: "Use letters and numbers only.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (DEFAULT_PROFANE_SET.has(word) || customProfane.some((item) => foldWord(item) === word)) {
      toast({
        title: "Already in the list",
        description: word,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setCustomProfane((items) => [...items, word]);
    setProfaneInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!reservedWords.length) {
        toast({
          title: "Add a reserved name",
          description: "The reserved list cannot be empty.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      await setDoc(doc(db, "systemConfig", "forbiddenSubdomains"), {
        reserved: reservedWords,
        reservedManaged: true,
        profane: [...DEFAULT_PROFANE, ...customProfane],
        updatedAt: serverTimestamp(),
        updatedBy: "super_admin",
      });
      toast({
        title: "Saved",
        description: "Stores can no longer choose these subdomains.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving forbidden subdomains:", error);
      toast({
        title: "Error",
        description: "Failed to save forbidden words.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageShell title="Forbidden Subdomains">
        <Spinner />
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Forbidden Subdomains"
      description="Add or remove a reserved name, then save. A store cannot use that subdomain."
    >
      <VStack align="stretch" spacing={6}>

        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
          <Text fontWeight="semibold" mb={1}>Reserved words</Text>
          <Text fontSize="sm" color="gray.500" mb={4}>
            These names are saved for the platform. Add a name or remove one, then save.
          </Text>
          <HStack mb={4}>
            <Input
              value={reservedInput}
              placeholder="Add a reserved name"
              onChange={(event) => setReservedInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") addReservedWord();
              }}
            />
            <Button onClick={addReservedWord}>Add</Button>
          </HStack>
          <Wrap spacing={2}>
            {reservedWords.map((word) => (
              <HStack key={word} spacing={1} bg="gray.100" borderRadius="full" px={3} py={1}>
                <Text fontSize="sm">{word}</Text>
                <IconButton
                  aria-label={`Remove ${word}`}
                  icon={<CloseIcon boxSize={2} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setReservedWords((items) => items.filter((item) => item !== word))}
                />
              </HStack>
            ))}
          </Wrap>
        </Box>

        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
          <Text fontWeight="semibold" mb={1}>Not allowed</Text>
          <Text fontSize="sm" color="gray.500" mb={4}>
            A store cannot use a name that contains one of these words. The starting words stay. You can add more and delete only those.
          </Text>
          <HStack mb={4}>
            <Input
              value={profaneInput}
              placeholder="Add a word"
              onChange={(event) => setProfaneInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") addProfaneWord();
              }}
            />
            <Button onClick={addProfaneWord}>Add</Button>
          </HStack>
          <Wrap spacing={2}>
            {DEFAULT_PROFANE.map((word) => (
              <HStack key={word} spacing={1} bg="gray.100" borderRadius="full" px={3} py={1}>
                <Text fontSize="sm">{word}</Text>
              </HStack>
            ))}
            {customProfane.map((word) => (
              <HStack key={word} spacing={1} bg="gray.100" borderRadius="full" px={3} py={1}>
                <Text fontSize="sm">{word}</Text>
                <IconButton
                  aria-label={`Remove ${word}`}
                  icon={<CloseIcon boxSize={2} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setCustomProfane((items) => items.filter((item) => item !== word))}
                />
              </HStack>
            ))}
          </Wrap>
        </Box>

        <HStack>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
            Save
          </Button>
        </HStack>
      </VStack>
    </PageShell>
  );
};

export default ForbiddenSubdomainSettings;
